/**
 * SI Prefix Utilities
 *
 * Functions for working with SI prefixes in MethodScript values.
 * MethodScript uses SI prefixes instead of decimal points.
 */

import { SI_PREFIXES } from './types.js';
import type { SIPrefix } from './types.js';

/**
 * Parse an SI-prefixed value string (e.g., "100m", "1500k", "-5u")
 * Returns the numeric value or null if not a valid SI-prefixed value.
 */
export function parseSIPrefixedValue(value: string): number | null {
    const match = /^(-?\d+)([afpnumkMGTPE])$/.exec(value);
    if (!match) {
        return null;
    }

    const numPart = parseInt(match[1], 10);
    const prefix = match[2];
    const prefixInfo = SI_PREFIXES[prefix];

    if (!prefixInfo) {
        return null;
    }

    return numPart * Math.pow(10, prefixInfo.exponent);
}

/**
 * Format a number with SI prefix
 * Automatically selects the most appropriate prefix.
 */
export function formatWithSIPrefix(value: number): string {
    if (value === 0) {
        return '0';
    }

    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    // Sort prefixes by exponent (descending)
    const sortedPrefixes = Object.values(SI_PREFIXES).sort((a, b) => b.exponent - a.exponent);

    for (const prefix of sortedPrefixes) {
        const divisor = Math.pow(10, prefix.exponent);
        if (absValue >= divisor && absValue / divisor >= 1) {
            const result = absValue / divisor;
            if (Number.isInteger(result)) {
                return `${sign}${result}${prefix.symbol}`;
            }
        }
    }

    // No prefix needed or value is fractional
    return value.toString();
}

/**
 * Get SI prefix info for a symbol
 */
export function getSIPrefixInfo(symbol: string): SIPrefix | null {
    return SI_PREFIXES[symbol] ?? null;
}

/**
 * Check if a string is a valid SI-prefixed value
 */
export function isSIPrefixedValue(value: string): boolean {
    return /^-?\d+[afpnumkMGTPE]$/.test(value);
}

/**
 * Format a number for display in hover documentation.
 * Uses exponential notation for very large or very small numbers.
 */
export function formatDisplayValue(actualValue: number): string {
    const absValue = Math.abs(actualValue);
    if (absValue >= 1e6 || (absValue < 1e-3 && actualValue !== 0)) {
        return actualValue.toExponential();
    }
    return actualValue.toString();
}

/**
 * Find all SI-prefixed values in a line of text.
 * Returns an array of matches with their text and index within the line.
 */
export function findSIPrefixedValues(text: string): Array<{ match: string; index: number }> {
    const regex = /\b-?\d+[afpnumkMGTPE]\b/g;
    const results: Array<{ match: string; index: number }> = [];
    let m;
    while ((m = regex.exec(text)) !== null) {
        results.push({ match: m[0], index: m.index });
    }
    return results;
}

/**
 * Create SI prefix hover content for a matched SI-prefixed value.
 * Returns markdown strings for title and detail, or null if not a valid SI prefix.
 *
 * @param match - The matched SI-prefixed value string (e.g., "100m", "5k", "-1u")
 * @returns Object with title and detail markdown strings, or null
 */
export function createSIPrefixHoverContent(match: string): { title: string; detail: string } | null {
    const prefix = match.slice(-1);
    const numPart = match.slice(0, -1);
    const prefixInfo = SI_PREFIXES[prefix];

    if (!prefixInfo) {
        return null;
    }

    const value = parseFloat(numPart);
    if (isNaN(value)) {
        return null;
    }

    const actualValue = value * Math.pow(10, prefixInfo.exponent);
    const exponentStr = `10^${prefixInfo.exponent}`;
    const displayValue = formatDisplayValue(actualValue);

    return {
        title: `**${match}** = ${numPart} × ${exponentStr} **(${prefixInfo.name})**`,
        detail: `= ${displayValue}`,
    };
}

/**
 * Get human-readable description of an SI-prefixed value
 * Example: "100m" -> "100 × 10^-3 (milli) = 0.1"
 */
export function describeSIPrefixedValue(value: string): string | null {
    const match = /^(-?\d+)([afpnumkMGTPE])$/.exec(value);
    if (!match) {
        return null;
    }

    const numPart = match[1];
    const prefix = match[2];
    const prefixInfo = SI_PREFIXES[prefix];

    if (!prefixInfo) {
        return null;
    }

    const numericValue = parseInt(numPart, 10);
    const actualValue = numericValue * Math.pow(10, prefixInfo.exponent);
    const displayValue = formatDisplayValue(actualValue);

    const exponentStr = `10^${prefixInfo.exponent}`;
    return `${value} = ${numPart} × ${exponentStr} (${prefixInfo.name}) = ${displayValue}`;
}
